import { XCircleIcon } from "@phosphor-icons/react";
import { Controller } from "react-hook-form";

import { FloatingButtons } from "~/v2/components/common/FloatingButtons";
import { CryptoCombobox } from "../../presentation/CryptoCombobox";
import type { CryptoSelectOption } from "../../presentation/CryptoSelect";
import type { UseAssetAddressFormReturn } from "./AssetAddressForm.controller";
import { AddressTextArea } from "./presentation/AddressTextArea";
import { NoSearchMatchEmptyState } from "./presentation/NoSearchMatchEmptyState";

interface AssetAddressFormProps extends UseAssetAddressFormReturn {
  chainOptions: CryptoSelectOption[];
  onCancel: () => void;
}

export const AssetAddressFormPresentation = ({
  control,
  onSubmit,
  isSubmitDisabled,
  onChainChange,
  onAssetChange,
  onAddressChange,
  onSwitchToMatchedChain,
  chainOptions,
  assetOptions,
  isAssetDisabled,
  addressValidation,
  onCancel,
}: AssetAddressFormProps) => (
  <form onSubmit={onSubmit} className="flex h-full flex-col gap-6">
    <div className="flex flex-col gap-4 rounded-lg border border-gray-300 bg-white p-4">
      <Controller
        name="chain"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <CryptoCombobox
            required
            label="Select or search chain"
            options={chainOptions}
            value={field.value}
            onChange={onChainChange}
            errorMessage={
              addressValidation.status === "mismatch"
                ? `Select ${addressValidation.matchedChainCode} Chain`
                : error?.message
            }
            emptyMessage={<NoSearchMatchEmptyState noun="chain" />}
            className="z-30 w-full"
          />
        )}
      />

      <Controller
        name="asset"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <CryptoCombobox
            required
            label="Select or search asset"
            options={assetOptions}
            value={field.value}
            onChange={onAssetChange}
            errorMessage={error?.message}
            emptyMessage={<NoSearchMatchEmptyState noun="asset" />}
            disabled={isAssetDisabled}
            className="z-20 w-full"
          />
        )}
      />

      <Controller
        name="address"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <AddressTextArea
            id="address"
            required
            label="Enter Address"
            value={field.value}
            placeholder="Paste or enter receiving address"
            onChange={onAddressChange}
            onBlur={field.onBlur}
            rightIcon={
              field.value ? (
                <button type="button" onClick={() => onAddressChange("")}>
                  <XCircleIcon size={20} />
                </button>
              ) : undefined
            }
            errorMsg={
              error?.message ? (
                addressValidation.status === "mismatch" ? (
                  <>
                    <span className="font-normal text-gray-700">
                      {`This is a valid ${addressValidation.switchTargetLabel} address, but you have ${addressValidation.currentPairLabel} selected. Would you like to switch to `}
                    </span>
                    <button
                      type="button"
                      onClick={onSwitchToMatchedChain}
                      className="font-medium text-blue-500 hover:text-blue-600"
                    >
                      {addressValidation.switchTargetLabel}?
                    </button>
                  </>
                ) : (
                  error.message
                )
              ) : undefined
            }
            successMsg={
              !error && addressValidation.status === "valid" ? (
                <span className="font-normal text-gray-700">
                  This address matches the{" "}
                  <span className="font-medium">
                    {addressValidation.chainName}
                  </span>{" "}
                  format. Please ensure this is the correct recipient before
                  sending.
                </span>
              ) : !error &&
                addressValidation.status === "validator-unavailable" ? (
                <span className="font-normal text-gray-700">
                  Address validation is not available for the{" "}
                  <span className="font-medium">
                    {addressValidation.chainName}
                  </span>{" "}
                  network. Please double-check the address before continuing —
                  crypto transfers are irreversible.
                </span>
              ) : undefined
            }
            className="w-full sm:min-h-10 md:h-10"
          />
        )}
      />
    </div>

    <FloatingButtons
      fixedOnBottom
      className="bg-white"
      next={{
        label: "Continue",
        type: "submit",
        disabled: isSubmitDisabled,
      }}
      back={{ onClick: onCancel, label: "Cancel" }}
    />
  </form>
);
